import React, { useState } from "react";
import { toast } from "sonner";
import { BellRing, Download, Loader2, PenLine, RefreshCw, XCircle } from "lucide-react";
import { api } from "../../lib/api";
import { contractActions, contractStatusOf, TONE } from "../../lib/contractStatus";

/**
 * One contract: where it stands, and the buttons that make sense for it.
 *
 * Shared by the enrollment drawer and the fictitious-data probe in Setări
 * curs, so both follow a contract the same way. Sending lives with the
 * caller — what gets sent differs, what happens next does not.
 *
 * Downloads carry a CNP (a real one, outside probes): fetched as blobs,
 * opened from memory, the object URL released shortly after.
 */
export const errorDetail = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  return fallback;
};

export const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("ro-RO");
};

export async function openBlob(request, fallbackName) {
  // Opened before the await: a window opened afterwards is a blocked popup.
  const win = window.open("", "_blank");
  try {
    const r = await request();
    const url = URL.createObjectURL(r.data);
    if (win) {
      win.location.href = url;
    } else {
      const a = document.createElement("a");
      a.href = url; a.download = fallbackName;
      document.body.appendChild(a); a.click(); a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    if (win) win.close();
    throw err;
  }
}

export const BTN = "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed";
export const BTN_PRIMARY = `${BTN} border-[#965EA7] bg-[#965EA7] text-white hover:bg-[#7A4986]`;
export const BTN_SECONDARY = `${BTN} border-[#965EA7] text-[#965EA7] hover:bg-[#F0E7F4]`;
export const BTN_QUIET = `${BTN} border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]`;

export default function ContractCard({ contract, onChanged, testIdPrefix = "contract" }) {
  const [busy, setBusy] = useState(null);
  if (!contract) return null;

  const actions = contractActions(contract);
  const status = contractStatusOf(contract);
  const spin = (key, Icon) => (busy === key ? <Loader2 size={14} className="animate-spin" /> : <Icon size={14} />);

  const run = async (key, fn, success) => {
    setBusy(key);
    try {
      const r = await fn();
      if (success) toast.success(typeof success === "function" ? success(r.data) : success);
    } catch (err) {
      toast.error(errorDetail(err, "Acțiunea nu a reușit."));
    } finally {
      setBusy(null);
      await onChanged?.();
    }
  };

  const cancel = () => {
    if (!window.confirm("Anulezi contractul? Nu mai poate fi semnat.")) return;
    run("cancel", () => api.post(`/admin/contracts/${contract.id}/cancel`), "Contractul a fost anulat.");
  };

  const download = async (kind) => {
    setBusy(`dl-${kind}`);
    try {
      await openBlob(
        () => api.get(`/admin/contracts/${contract.id}/download`, { params: { kind }, responseType: "blob" }),
        `${kind === "signed" ? "contract" : "certificat"}-${contract.number}.pdf`,
      );
    } catch {
      toast.error("Documentul nu a putut fi descărcat din arhivă.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid={`${testIdPrefix}-card`}>
      <div className="mt-3 rounded-lg border border-[#E5E7EB] bg-[#F5F6F6] p-3 text-sm" data-testid={`${testIdPrefix}-state`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-semibold text-[#373A45]">{contract.number || "—"}</span>
          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${TONE[status.tone]}`}>
            {status.label}
          </span>
        </div>
        <div className="mt-1.5 grid gap-0.5 text-xs text-[#6B7280]">
          {contract.sent_at && <span>Trimis: {fmtDateTime(contract.sent_at)}{contract.sandbox ? ` · către ${contract.sent_to}` : ""}</span>}
          {contract.participant_signed_at && <span>Semnat de participant: {fmtDateTime(contract.participant_signed_at)}</span>}
          {contract.completed_at && <span>Semnat complet: {fmtDateTime(contract.completed_at)}</span>}
          {contract.archived_at && <span>Arhivat: {fmtDateTime(contract.archived_at)}</span>}
          {contract.reminders > 0 && <span>Reamintiri: {contract.reminders} (ultima {fmtDateTime(contract.last_reminder_at)})</span>}
          {contract.rejection_reason && <span>Motivul refuzului: „{contract.rejection_reason}”</span>}
          {contract.fped_sign_error && contract.status === "participant_signed" && (
            <span className="text-[#9b1f2c]">Semnătura automată FPED nu a reușit: {contract.fped_sign_error}</span>
          )}
          {contract.error && <span className="text-[#9b1f2c]">{contract.error.message}</span>}
          {contract.last_synced_at && <span>Ultima verificare la eSemneaza: {fmtDateTime(contract.last_synced_at)}</span>}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {actions.refresh && (
          <button type="button" disabled={Boolean(busy)} className={BTN_QUIET} data-testid={`${testIdPrefix}-refresh-button`}
            onClick={() => run("refresh", () => api.post(`/admin/contracts/${contract.id}/refresh`), "Status actualizat.")}>
            {spin("refresh", RefreshCw)} Actualizează statusul
          </button>
        )}
        {actions.signFped && (
          <button type="button" disabled={Boolean(busy)} className={BTN_PRIMARY} data-testid={`${testIdPrefix}-sign-button`}
            onClick={() => run("sign", () => api.post(`/admin/contracts/${contract.id}/sign-fped`),
              (d) => (d.contract.status === "completed"
                ? "Semnat de FPED. Contractul e complet."
                : "Semnătura FPED a fost cerută; actualizează statusul peste un minut."))}>
            {spin("sign", PenLine)} Semnează pentru FPED
          </button>
        )}
        {actions.remind && (
          <button type="button" disabled={Boolean(busy)} className={BTN_QUIET} data-testid={`${testIdPrefix}-remind-button`}
            onClick={() => run("remind", () => api.post(`/admin/contracts/${contract.id}/remind`), "Reamintirea a fost trimisă.")}>
            {spin("remind", BellRing)} Retrimite reamintirea
          </button>
        )}
        {actions.download && (
          <>
            <button type="button" disabled={Boolean(busy)} className={BTN_SECONDARY} onClick={() => download("signed")}
              data-testid={`${testIdPrefix}-download-signed`}>
              {spin("dl-signed", Download)} Contractul semnat
            </button>
            <button type="button" disabled={Boolean(busy)} className={BTN_QUIET} onClick={() => download("certificate")}
              data-testid={`${testIdPrefix}-download-certificate`}>
              {spin("dl-certificate", Download)} Certificatul de semnare
            </button>
          </>
        )}
        {actions.cancel && (
          <button type="button" disabled={Boolean(busy)} onClick={cancel}
            className={`${BTN} border-[#9b1f2c]/40 text-[#9b1f2c] hover:bg-[#FDECEC]`} data-testid={`${testIdPrefix}-cancel-button`}>
            {spin("cancel", XCircle)} Anulează
          </button>
        )}
      </div>
    </div>
  );
}
